/**
 * Product Adapters
 *
 * Copyright Deskshare Inc.
 * All rights reserved.
 *
 * This software is proprietary and confidential.
 * Unauthorized copying, distribution, or use is strictly prohibited.
 *
 * Adapters for combining products and extracting detail views.
 */

import { getProductDownloadUrl } from "@/lib/constants";
import { getStripeToQlm } from "@/services/productIdConfig";
import { isLegacyLicense } from "@/lib/license-matching";
import { License } from "@/types";
import { logger } from "@/lib/logger";
import i18n from "@/i18n";
import { getPurchasedDeviceCount } from "@/lib/licensing";
import type {
  ActiveSubscription,
  DeviceDetails,
  LicenseSummary,
  NextPaymentProduct,
  OneTimePurchase,
  Product,
  PurchasedProduct,
  SubscriptionDetails,
  SubscriptionProduct,
} from "./adapter-types";

/**
 * Merge the two sources the catalogue is built from into one product list.
 *
 * The shop bills two ways and each way is described by a different Stripe
 * record, so each has its own adapter: subscriptions come from the customer's
 * subscription items, one-time purchases from invoice lines. Neither source
 * knows about the other, and a purchase adapted by neither is a purchase the
 * customer paid for and cannot see -- so both are required for My Products to
 * be complete.
 *
 * `oneTimePurchases` defaults to an empty list because an account may genuinely
 * hold none, and because the caller guards each adaptation separately: a
 * failure in one source must not empty the catalogue of the other.
 */
export const adaptProducts = async (
  subscriptionProducts: SubscriptionProduct[],
  oneTimePurchases: OneTimePurchase[] = [],
): Promise<Product[]> => {
  if (!subscriptionProducts) {
    throw new Error("A subscription product array must be provided");
  }

  const adaptedProducts: Product[] = [];

  if (subscriptionProducts && Array.isArray(subscriptionProducts)) {
    for (const subProduct of subscriptionProducts) {
      if (!subProduct) continue;
      adaptedProducts.push({
        id: subProduct.id,
        // VD-2 (Pass 20): the row key has to survive this hop, or the dialog is
        // back to addressing a product and one row opens the other's licences.
        rowKey: subProduct.rowKey ?? subProduct.productId,
        productId: subProduct.productId,
        productName: subProduct.productName,
        licenses: subProduct.licenses || [],
        totalLicenses: subProduct.totalLicenses || 0,
        // S657, "from N purchases": the count has to survive this hop, or the view model is
        // back to counting keys.
        purchaseCount: subProduct.purchaseCount,
        type: "recurring",
        interval: subProduct.interval,
        totalDevices: subProduct.totalDevices || 0,
        availableDevices: subProduct.availableDevices || 0,
        createdAt: subProduct.createdAt,
        // CHANGE_06 (FCR S483): pass through non-active status for display
        displayStatus: (subProduct as Product).displayStatus,
      });
    }
  }

  if (oneTimePurchases && Array.isArray(oneTimePurchases)) {
    for (const purchase of oneTimePurchases) {
      if (!purchase) continue;
      adaptedProducts.push({
        id: purchase.id,
        // VD-2 (Pass 20): the row key has to survive this hop, or the dialog is
        // back to addressing a product and one row opens the other's licences.
        rowKey: purchase.rowKey ?? purchase.productId,
        productId: purchase.productId,
        productName: purchase.productName,
        licenses: purchase.licenses || [],
        totalLicenses: purchase.totalLicenses || 0,
        // S657, "from N purchases": as above.
        purchaseCount: purchase.purchaseCount,
        // A one-time purchase has no billing cycle, so `interval` is left unset
        // rather than filled with a placeholder. Every surface that labels a
        // product reads this pair together: `type` says what kind of purchase
        // it is, `interval` how often it is billed, and an absent interval is
        // the honest answer for a purchase that is billed once.
        type: "one_time",
        totalDevices: purchase.totalDevices || 0,
        availableDevices: purchase.availableDevices || 0,
        createdAt: purchase.createdAt,
      });
    }
  }

  logger.info(
    `Adapted ${adaptedProducts.length} total products from ` +
      `${subscriptionProducts?.length || 0} subscription(s) and ` +
      `${oneTimePurchases?.length || 0} one-time purchase(s)`,
  );

  // SU-10 (FCR Pass 11): De-duplicate, preferring the entry that carries licence
  // data. The same purchase can be described by more than one source row;
  // without this it renders twice — one of the two showing 0 licences and 0/0
  // devices.
  // SU-20 (FCR Pass 9): key on productId AND plan type, so two entitlements of
  // one product that differ in kind are never treated as a duplicate of each
  // other. A genuine duplicate has the same type in both entries and still
  // collapses correctly.
  const byProductAndType = new Map<string, Product>();
  for (const product of adaptedProducts) {
    const key = `${product.productId}:${product.type}`;
    const existing = byProductAndType.get(key);
    if (!existing) {
      byProductAndType.set(key, product);
      continue;
    }
    const existingHasLicenses = (existing.licenses?.length ?? 0) > 0;
    const candidateHasLicenses = (product.licenses?.length ?? 0) > 0;
    if (!existingHasLicenses && candidateHasLicenses) {
      byProductAndType.set(key, product);
    }
  }
  const deduped = Array.from(byProductAndType.values());
  if (deduped.length < adaptedProducts.length) {
    logger.info(
      `adaptProducts: merged ${adaptedProducts.length - deduped.length} duplicate(s) by productId and plan type`,
    );
  }
  return deduped;
};

export const adaptPurchasedProducts = (
  products: Product[],
): PurchasedProduct[] => {
  if (!products || products.length === 0) {
    throw new Error(
      "Products data is required to adapt to purchased products.",
    );
  }

  return products.map((product) => ({
    productName: product.productName,
    totalLicenses: product.totalLicenses,
    type: product.type === "recurring" ? "subscription" : "one_time",
  }));
};

export const adaptNextPaymentProducts = (
  products: ActiveSubscription[],
): NextPaymentProduct[] => {
  if (!products || products.length === 0) {
    return [];
  }

  return products
    .filter((product) => product.status !== "canceled") // B1-2 (FCR S476): fully cancelled subscriptions have no upcoming payment
    .filter((product) => !product.cancelAtPeriodEnd) // S-2c: exclude cancelling subscriptions from next-payment widget
    .filter((product) => product.status !== "past_due") // S417: past_due subscriptions have a failed payment -- they already surface
    // via the grace period banner on the billing page. Showing them in the
    // "Next Payment" widget implies an upcoming charge that will succeed, which
    // is misleading. Industry practice (Stripe dashboard, Shopify) excludes
    // past_due from upcoming-payment displays.
    .map((product) => ({
      id: product.id,
      productName: product.productName,
      nextPaymentDate: product.renewalDate,
      amount: product.amount,
      currency: product.currency,
    }));
};

export const adaptLicenseSummary = (
  products: Product[],
  license: License,
): LicenseSummary => {
  if (!license) {
    throw new Error("License data is required to adapt to license summary.");
  }

  let product = products.find((p) => p.productId === license.StripeProductId);

  // Intelligent matching for legacy licenses
  if (!product && !license.StripeProductId) {
    product = products.find((p) =>
      p.licenses?.some((l) => l.ActivationKey === license.ActivationKey),
    );

    if (!product && isLegacyLicense(license)) {
      product = products.find((p) => {
        const expectedQlmId = getStripeToQlm()[p.productId];
        // NF-2 guard: 0 is a placeholder for unified products whose QLM ID is
        // not yet configured. Treat 0 as "no mapping" to avoid false matches.
        return (
          expectedQlmId !== undefined && expectedQlmId > 0 && license.ProductID === expectedQlmId
        );
      });
      if (product) {
        logger.info(
          `[LicenseSummary] Matched legacy license ${license.ActivationKey} to product ${product.productId} by ProductID`,
        );
      }
    }
  }

  if (!product) {
    logger.warn(
      `Product not found for license ${license.ActivationKey}, using fallback`,
    );
    // SU-12 / SU-13 (FCR S557): purchased quantity drives both the display and
    // the availability figure. AvailableLicenses is QLM's own count and includes
    // the reinstall buffer seat, so it cannot be used here.
    const calculatedNumLicenses = getPurchasedDeviceCount(license);
    const calculatedAvailable = Math.max(
      0,
      calculatedNumLicenses - (license.ActivationCount ?? 0),
    );
    return {
      productName: i18n.t("common.unknownProduct"),
      activationKey: license.ActivationKey,
      version: license.MajorVersion,
      numLicenses: calculatedNumLicenses,
      availableLicenses: calculatedAvailable,
      downloadUrl: license.StripeProductId ? getProductDownloadUrl(license.StripeProductId) : "",
      type: "one_time",
      subscriptionExpiryDate: license.SubscriptionExpiryDate || undefined,
    };
  }

  // SU-12 / SU-13 (FCR S557): see the fallback branch above.
  const calculatedNumLicenses = getPurchasedDeviceCount(license);
  const calculatedAvailable = Math.max(
    0,
    calculatedNumLicenses - (license.ActivationCount ?? 0),
  );

  // Issue 6 (FCR Pass 6): a licence carrying no SubscriptionExpiryDate is not a
  // subscription entitlement, whatever the catalogue says. Infer the runtime
  // type so the suffix shown on the dashboard is not "Annual License" for a key
  // that never expires.
  const inferredType: "recurring" | "one_time" =
    product.type === "recurring" && !license.SubscriptionExpiryDate
      ? "one_time"
      : product.type;

  return {
    productName: product.productName,
    activationKey: license.ActivationKey,
    version: license.MajorVersion,
    numLicenses: calculatedNumLicenses,
    availableLicenses: calculatedAvailable,
    downloadUrl: getProductDownloadUrl(product.productId),
    type: inferredType,
    subscriptionExpiryDate: license.SubscriptionExpiryDate || undefined,
  };
};

export const adaptSubscriptionDetails = (
  subscription: ActiveSubscription,
): SubscriptionDetails => {
  if (!subscription) {
    throw new Error(
      "Subscription data is required to adapt to subscription details.",
    );
  }
  return {
    productId: subscription.productId,
    interval: subscription.interval,
    renewalDate: subscription.renewalDate,
    amountDisplay: subscription.amountDisplay,
    startDate: subscription.startDate,
  };
};

export const adaptDeviceTable = (
  devices: License["Devices"],
): DeviceDetails[] => {
  if (!devices || !Array.isArray(devices)) {
    return [];
  }

  return devices.map((device) => {
    const rawName = (device?.ComputerName ?? "").trim();
    return {
      deviceId: device?.ComputerID ?? "",
      deviceName: rawName || i18n.t("common.unknownDevice"),
      activatedOn: device?.ActivationDate ?? "",
      lastActive: device?.LastAccessedDate ?? "",
      ip: device?.IP_Address ?? "",
      // A1 (FCR S497): layered offline detection — first available signal wins.
      //  1) IsOffline  -- back-end stamp (A2 fix, most reliable)
      //  2) is_offline -- QLM-derived 0/1 flag from OfflineExpiryDate
      //  3) OfflineExpiryDate present -- direct QLM offline expiry field
      //  4) blank ComputerName -- offline activation page never submits a name
      isOffline:
        device?.IsOffline === true ||
        Boolean(device?.is_offline) ||
        device?.OfflineExpiryDate != null ||
        rawName === "",
    };
  });
};
